import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  createComponent,
  DestroyRef,
  EnvironmentInjector,
  Inject,
  Injectable,
  Injector,
  Renderer2,
  RendererFactory2,
  Type,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BodyScrollLockService } from '@shared/services';
import { KeyWithBreakpoint, ModalCloseStrategy, ModalTypes, SdModalOptionsWithBreakpoints } from '@shared/v2/models';
import { WindowRef } from '@spartacus/core';
import { BREAKPOINT } from '@spartacus/storefront';
import { fromEvent } from 'rxjs';
import { SdModal } from './modal';
import { SD_MODAL_OPTIONS_TOKEN } from './modal-options.token';
import { SdvorModalComponent } from './modal.component';

@Injectable({
  providedIn: 'root',
})
export class SdModalService {
  private stack: SdModal[] = [];
  private renderer: Renderer2;

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly injector: EnvironmentInjector,
    private readonly bodyScrollLockService: BodyScrollLockService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly windowRef: WindowRef,
    private readonly destroyRef: DestroyRef,
    rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    if (this.windowRef.isBrowser()) {
      fromEvent<PopStateEvent>(this.windowRef.nativeWindow, 'popstate')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.closeActiveModal());
    }
  }

  open(content: Type<any>, options: SdModalOptionsWithBreakpoints = {}): SdModal {
    options.maxWidth = this.fillPropertyWithBreakpoints(options.maxWidth, '100%');
    options.modalType = this.fillPropertyWithBreakpoints(options.modalType, ModalTypes.Compact);
    options.hideCloseButton = this.fillPropertyWithBreakpoints(options.hideCloseButton, false);
    options.title = this.fillPropertyWithBreakpoints(options.title, '');
    options.ignoreOutsideClick = this.fillPropertyWithBreakpoints(options.ignoreOutsideClick, false);
    options.closeStrategy = this.fillPropertyWithBreakpoints(options.closeStrategy, ModalCloseStrategy.Outside);

    const sdModal = new SdModal();
    const elementInjector = Injector.create({
      providers: [
        { provide: SdModal, useValue: sdModal },
        { provide: SD_MODAL_OPTIONS_TOKEN, useValue: options },
      ],
      parent: options.injector ?? this.injector,
    });

    const contentRef = createComponent(content, {
      environmentInjector: this.injector,
      elementInjector,
    });

    const modalRef = createComponent(SdvorModalComponent, {
      environmentInjector: this.injector,
      elementInjector,
      projectableNodes: [[contentRef.location.nativeElement]],
    });

    sdModal.setContentRef(contentRef);
    sdModal.setModalRef(modalRef);
    sdModal.setOnCloseMethod(() => {
      const index = this.stack.indexOf(sdModal);

      if (index === -1) {
        return;
      }

      const instance = this.stack[index];

      this.appRef.detachView(instance.contentRef.hostView);
      this.appRef.detachView(instance.modalRef.hostView);
      instance.contentRef.destroy();
      instance.modalRef.destroy();
      this.stack.splice(index, 1);

      if (this.stack.length === 0) {
        this.bodyScrollLockService.unlock();
        this.renderer.removeClass(this.document.body, 'sd-modal--opened');
      }
    });

    this.bodyScrollLockService.lock();
    this.renderer.addClass(this.document.body, 'sd-modal--opened');
    this.document.body.appendChild(modalRef.location.nativeElement);
    this.appRef.attachView(modalRef.hostView);
    this.appRef.attachView(contentRef.hostView);
    this.stack.push(sdModal);

    return sdModal;
  }

  closeActiveModal(): void {
    const sdModal = this.stack.at(-1);

    if (!sdModal) {
      return;
    }

    sdModal.close();
  }

  getActiveModal(): SdModal | undefined {
    return this.stack.at(-1);
  }

  hasOpenModals(): boolean {
    return this.stack.length > 0;
  }

  private fillPropertyWithBreakpoints<T extends string | number | boolean | KeyWithBreakpoint<T>>(
    property: any,
    defaultValue: T,
  ): KeyWithBreakpoint<T> {
    let formattedProperty: KeyWithBreakpoint<T> = {};

    if (typeof property === 'object') {
      formattedProperty = Object.assign({}, property);
    } else if (property !== undefined) {
      formattedProperty[BREAKPOINT.xxs] = property;
    } else {
      formattedProperty[BREAKPOINT.xxs] = defaultValue;
    }

    Object.values(BREAKPOINT).forEach((breakpoint) => {
      if (!formattedProperty[breakpoint]) {
        formattedProperty[breakpoint] = defaultValue;
      }

      defaultValue = formattedProperty[breakpoint];
    });

    return formattedProperty;
  }
}
